/**
 * Workout Session Validation Schemas
 *
 * Zod schemas for validating workout session tracking and related data.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Note: For workout/plan template CRUD operations, use template.schemas.ts instead.
 * Note: For custom exercise schemas, use custom-exercise.schemas.ts instead.
 *
 * Features:
 * - Workout session validation (active session tracking with sets)
 * - Shared session validation
 * - Session state validation
 */

import {
  groupTypeSchema,
  trackingTypeSchema,
  templateNameSchema as nameSchema,
} from './common.schemas';
import { z } from './zod';

/**
 * Workout effort rating validation (shared-session variant — optional with a
 * neutral default, matching the accept/decline flow)
 */
const effortRatingSchema = z
  .number({
    error: (issue) =>
      issue.input === undefined
        ? 'Effort rating must be at least 1'
        : undefined,
  })
  .min(1, 'Effort rating must be at least 1')
  .max(10, 'Effort rating must be at most 10')
  .optional()
  .default(5);

/**
 * Effort rating for submitting a live session.
 */
const sessionEffortRatingSchema = z
  .number()
  .int('Effort rating must be a whole number')
  .min(1, 'Effort rating must be at least 1')
  .max(10, 'Effort rating must be at most 10');

// =============================================================================
// WORKOUT SESSION VALIDATION SCHEMAS
// =============================================================================

/**
 * Base set schema with common fields.
 *
 * Strict, and deliberately without `id`: the API spreads each set object
 * straight into the completed-set insert, so any stray key would reach the
 * database. Skipped sets are OMITTED from the payload, never zeroed — every
 * metric the API accepts is min:1.
 */
const baseSetSchema = z
  .object({
    reps: z.number().int().min(1).optional(),
    weight: z.number().min(1).optional(),
    duration: z.number().min(1).optional(),
    distance: z.number().min(1).optional(),
  })
  .strict();

/**
 * Reusable set schemas for each tracking type.
 * Each required metric carries its message for an ABSENT key as well as a
 * low one — otherwise Zod falls back to a bare "Required".
 * Non-empty: the API's `required` rule rejects an empty sets array — an
 * exercise with nothing logged is omitted from the payload entirely.
 */
const repsOnlySetSchema = z
  .array(
    baseSetSchema.extend({
      reps: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Reps are required for reps-only exercises'
              : undefined,
        })
        .int()
        .min(1, 'Reps are required for reps-only exercises'),
    })
  )
  .min(1, 'At least one set is required');

const weightRepsSetSchema = z
  .array(
    baseSetSchema.extend({
      weight: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Weight is required for weight & reps exercises'
              : undefined,
        })
        .min(1, 'Weight is required for weight & reps exercises'),
      reps: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Reps are required for weight & reps exercises'
              : undefined,
        })
        .int()
        .min(1, 'Reps are required for weight & reps exercises'),
    })
  )
  .min(1, 'At least one set is required');

const timeBasedSetSchema = z
  .array(
    baseSetSchema.extend({
      duration: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Duration is required for time-based exercises'
              : undefined,
        })
        .min(1, 'Duration is required for time-based exercises'),
    })
  )
  .min(1, 'At least one set is required');

const distanceBasedSetSchema = z
  .array(
    baseSetSchema.extend({
      distance: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Distance is required for distance-based exercises'
              : undefined,
        })
        .min(1, 'Distance is required for distance-based exercises'),
    })
  )
  .min(1, 'At least one set is required');

const timeDistanceSetSchema = z
  .array(
    baseSetSchema.extend({
      duration: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Duration is required for time & distance exercises'
              : undefined,
        })
        .min(1, 'Duration is required for time & distance exercises'),
      distance: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Distance is required for time & distance exercises'
              : undefined,
        })
        .min(1, 'Distance is required for time & distance exercises'),
    })
  )
  .min(1, 'At least one set is required');

const weightTimeSetSchema = z
  .array(
    baseSetSchema.extend({
      weight: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Weight is required for weight & time exercises'
              : undefined,
        })
        .min(1, 'Weight is required for weight & time exercises'),
      duration: z
        .number({
          error: (issue) =>
            issue.input === undefined
              ? 'Duration is required for weight & time exercises'
              : undefined,
        })
        .min(1, 'Duration is required for weight & time exercises'),
    })
  )
  .min(1, 'At least one set is required');

/**
 * Exercise member schema for grouped exercises (superset, triset, circuit, giant_set)
 */
const exerciseMemberSchema = z.discriminatedUnion('tracking_type', [
  // Reps only exercises
  z.object({
    name: nameSchema,
    guid: z.string().min(1, 'Invalid GUID'),
    tracking_type: z.literal('reps_only'),
    sets: repsOnlySetSchema,
  }),

  // Weight and reps exercises
  z.object({
    name: nameSchema,
    guid: z.string().min(1, 'Invalid GUID'),
    tracking_type: z.literal('weight_reps'),
    sets: weightRepsSetSchema,
  }),

  // Time-based exercises
  z.object({
    name: nameSchema,
    guid: z.string().min(1, 'Invalid GUID'),
    tracking_type: z.literal('time_based'),
    sets: timeBasedSetSchema,
  }),

  // Distance-based exercises
  z.object({
    name: nameSchema,
    guid: z.string().min(1, 'Invalid GUID'),
    tracking_type: z.literal('distance_based'),
    sets: distanceBasedSetSchema,
  }),

  // Time and distance exercises
  z.object({
    name: nameSchema,
    guid: z.string().min(1, 'Invalid GUID'),
    tracking_type: z.literal('time_distance'),
    sets: timeDistanceSetSchema,
  }),

  // Weight and time exercises
  z.object({
    name: nameSchema,
    guid: z.string().min(1, 'Invalid GUID'),
    tracking_type: z.literal('weight_time'),
    sets: weightTimeSetSchema,
  }),
]);

/**
 * One top-level exercise entry in the stop payload.
 *
 * Mirrors the API contract (WorkoutCompletedRequest): every entry — grouped or
 * not — is a GROUP LEADER carrying its own `sets`, with `members` holding the
 * REST of the group (so a 2-exercise superset is leader + 1 member). The wire
 * key for grouping is `group_type` (the request's `array:` allowlist rejects
 * anything else).
 */
const sessionExerciseEntry = <
  TTracking extends z.core.util.Literal,
  TSets extends z.ZodTypeAny,
>(
  trackingType: TTracking,
  setsSchema: TSets
): z.ZodObject<{
  name: typeof nameSchema;
  guid: z.ZodString;
  wildcard: z.ZodDefault<z.ZodOptional<z.ZodBoolean>>;
  group_type: z.ZodDefault<z.ZodOptional<typeof groupTypeSchema>>;
  tracking_type: z.ZodLiteral<TTracking>;
  sets: TSets;
  members: z.ZodOptional<z.ZodArray<typeof exerciseMemberSchema>>;
}> =>
  z.object({
    name: nameSchema,
    guid: z.string().min(1, 'Invalid GUID'),
    wildcard: z.boolean().optional().default(false),
    group_type: groupTypeSchema.optional().default('regular'),
    tracking_type: z.literal(trackingType),
    sets: setsSchema,
    members: z.array(exerciseMemberSchema).optional(),
  });

/**
 * Group-size coherence, matching the API's GroupType semantics
 * (size = leader + members).
 */
const groupSizeRules: Partial<
  Record<string, { min: number; max: number; message: string }>
> = {
  regular: {
    min: 1,
    max: 1,
    message: 'Regular exercises cannot have group members',
  },
  superset: {
    min: 2,
    max: 2,
    message: 'Supersets must have exactly 2 exercises (leader + 1 member)',
  },
  triset: {
    min: 3,
    max: 3,
    message: 'Trisets must have exactly 3 exercises (leader + 2 members)',
  },
  circuit: {
    min: 4,
    max: 5,
    message: 'Circuits must have 4-5 exercises (leader + members)',
  },
  giant_set: {
    min: 6,
    max: Number.POSITIVE_INFINITY,
    message: 'Giant sets must have at least 6 exercises (leader + members)',
  },
  // emom: no size constraint
};

/** One stop-payload exercise entry, whichever tracking type it carries. */
const sessionExerciseEntrySchema = z.discriminatedUnion('tracking_type', [
  sessionExerciseEntry('reps_only', repsOnlySetSchema),
  sessionExerciseEntry('weight_reps', weightRepsSetSchema),
  sessionExerciseEntry('time_based', timeBasedSetSchema),
  sessionExerciseEntry('distance_based', distanceBasedSetSchema),
  sessionExerciseEntry('time_distance', timeDistanceSetSchema),
  sessionExerciseEntry('weight_time', weightTimeSetSchema),
]);

/**
 * Every group in a list of exercise entries is sized the way its
 * `group_type` says. Shared by the host's exercises and each guest's.
 */
const refineGroupSizes = (
  exercises: z.infer<typeof sessionExerciseEntrySchema>[],
  ctx: z.RefinementCtx
): void => {
  exercises.forEach((exercise, index) => {
    const rule = groupSizeRules[exercise.group_type ?? 'regular'];
    if (!rule) return;
    const size = 1 + (exercise.members?.length ?? 0);
    if (size < rule.min || size > rule.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: rule.message,
        path: [index, 'members'],
      });
    }
  });
};

/** Mirrors `MAX_SESSION_GUESTS` in the session engine (the crew limit). */
const MAX_STOP_PAYLOAD_GUESTS = 5;

/**
 * Workout session form validation schema (the stop payload)
 */
export const workoutSessionSchema = z.object({
  effort_rating: sessionEffortRatingSchema,
  is_deload: z.boolean().optional(),
  /** Time spent paused, in whole seconds — the API subtracts it from duration */
  paused_seconds: z
    .number()
    .int('Paused seconds must be a whole number')
    .min(0, 'Paused seconds cannot be negative')
    .optional(),
  exercises: z
    .array(sessionExerciseEntrySchema)
    .min(1, 'At least one exercise is required')
    .superRefine(refineGroupSizes),
  /**
   * Each guest's performed sets, keyed by username. Every crew member has a
   * key — possibly with no exercises — so the keys are also the invitation
   * list. A guest's entries follow the host's per-type set rules.
   */
  guests: z
    .record(
      z.string().min(1, 'Guest name is required'),
      z.object({
        exercises: z
          .array(sessionExerciseEntrySchema)
          .superRefine(refineGroupSizes),
      })
    )
    .refine(
      (guests) => Object.keys(guests).length <= MAX_STOP_PAYLOAD_GUESTS,
      'A session can have at most 5 guests'
    )
    .optional(),
});

// =============================================================================
// SHARED SESSION VALIDATION SCHEMAS
// =============================================================================

export const sharedSessionSchema = z.object({
  action: z.enum(['accept', 'decline']),
  effort_rating: effortRatingSchema,
});

// =============================================================================
// SESSION STATE VALIDATION SCHEMAS
// =============================================================================

/**
 * Session status enum schema
 */
const sessionStatusSchema = z.enum([
  'active',
  'paused',
  'completed',
  'abandoned',
  'cancelled',
]);

/**
 * Session guest schema
 */
const sessionGuestSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  avatar_url: z.string().optional(),
});

/**
 * Session exercise set schema (for session state validation)
 */
const sessionExerciseSetSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  reps: z.number().nullable().optional(),
  weight: z.number().nullable().optional(),
  distance: z.number().nullable().optional(),
  duration: z.number().nullable().optional(),
});

/**
 * Base session exercise schema (without members for non-recursive use)
 */
const baseSessionExerciseSchema = z.object({
  guid: z.union([z.string(), z.number()]),
  name: z.string().min(1, 'Name is required'),
  tracking_type: trackingTypeSchema,
  /** Wire field from PreWorkoutResource; null reads as 'regular' */
  group_type: groupTypeSchema.nullable().optional(),
  wildcard: z.boolean(),
  gif_url: z.string().optional(),
  order: z.number().optional(),
  sets: z.array(sessionExerciseSetSchema).nullable().optional(),
  targets: z.array(sessionExerciseSetSchema).nullable().optional(),
});

/**
 * Session exercise schema with recursive members support
 * Uses z.lazy for self-referencing structure
 */
type SessionExercise = z.infer<typeof baseSessionExerciseSchema> & {
  members?: SessionExercise[] | null;
};

const sessionExerciseSchema: z.ZodType<SessionExercise, SessionExercise> =
  baseSessionExerciseSchema.extend({
    members: z
      .lazy(() => z.array(sessionExerciseSchema))
      .nullable()
      .optional(),
  });

/**
 * Session workout schema
 */
const sessionWorkoutSchema = z.object({
  workout_id: z.union([z.string(), z.number()]),
  start_time: z.number().positive('Start time must be a positive number'),
  exercises: z.array(sessionExerciseSchema),
  guests: z.array(sessionGuestSchema).nullable(),
});

/**
 * Session state schema for validating SessionState objects
 */
export const sessionStateSchema = z.object({
  start: z.number().positive('Start must be a positive number'),
  active: z.boolean(),
  state: sessionStatusSchema,
  workout: sessionWorkoutSchema,
  completed_exercises: z.array(sessionExerciseSchema).nullable(),
  duration: z
    .number()
    .positive('Duration must be a positive number')
    .nullable()
    .optional(),
  abandoned_at: z
    .number()
    .positive('Abandoned timestamp must be a positive number')
    .nullable()
    .optional(),
  /** Platform-owned engine snapshot — round-tripped, never interpreted here */
  client_state: z.record(z.string(), z.unknown()).nullable().optional(),
});

// =============================================================================
// UPDATE SCHEMAS
// =============================================================================

/**
 * Update shared session schema (partial for PATCH requests).
 * zod 4 applies a default even inside .partial(), so the field's default is
 * unwrapped here: a key the caller leaves out stays out of the update.
 */
export const updateSharedSessionSchema = sharedSessionSchema
  .extend({ effort_rating: effortRatingSchema.unwrap() })
  .partial();

// =============================================================================
// TYPE EXPORTS
// =============================================================================

// Workout session types
export type WorkoutSessionInput = z.input<typeof workoutSessionSchema>;
export type WorkoutSessionOutput = z.output<typeof workoutSessionSchema>;
/** One exercise entry of the stop payload — the host's or a guest's */
export type SessionExerciseEntry = z.input<typeof sessionExerciseEntrySchema>;

// Shared session types
export type SharedSessionInput = z.input<typeof sharedSessionSchema>;
export type SharedSessionOutput = z.output<typeof sharedSessionSchema>;
export type UpdateSharedSessionInput = z.input<
  typeof updateSharedSessionSchema
>;
export type UpdateSharedSessionOutput = z.output<
  typeof updateSharedSessionSchema
>;

// Session state types
export type SessionStateInput = z.input<typeof sessionStateSchema>;
export type SessionStateOutput = z.output<typeof sessionStateSchema>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

// --- Workout session validation ---

/**
 * Validate workout session data
 * @param data - The data to validate
 * @returns The validated and typed workout session data
 * @throws ZodError if validation fails
 */
export function validateWorkoutSession(data: unknown): WorkoutSessionOutput {
  return workoutSessionSchema.parse(data);
}

/**
 * Safely validate workout session data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateWorkoutSession(data: unknown) {
  return workoutSessionSchema.safeParse(data);
}

/**
 * Check if data is valid workout session data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidWorkoutSession(data: unknown): boolean {
  return workoutSessionSchema.safeParse(data).success;
}

// --- Shared session validation ---

/**
 * Validate shared session data
 * @param data - The data to validate
 * @returns The validated and typed shared session data
 * @throws ZodError if validation fails
 */
export function validateSharedSession(data: unknown): SharedSessionOutput {
  return sharedSessionSchema.parse(data);
}

/**
 * Safely validate shared session data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateSharedSession(data: unknown) {
  return sharedSessionSchema.safeParse(data);
}

/**
 * Check if data is valid shared session data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidSharedSession(data: unknown): boolean {
  return sharedSessionSchema.safeParse(data).success;
}

/**
 * Validate update shared session data
 * @param data - The data to validate
 * @returns The validated and typed update shared session data
 * @throws ZodError if validation fails
 */
export function validateUpdateSharedSession(
  data: unknown
): UpdateSharedSessionOutput {
  return updateSharedSessionSchema.parse(data);
}

/**
 * Safely validate update shared session data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateSharedSession(data: unknown) {
  return updateSharedSessionSchema.safeParse(data);
}

/**
 * Check if data is valid update shared session data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateSharedSession(data: unknown): boolean {
  return updateSharedSessionSchema.safeParse(data).success;
}

// --- Session state validation ---

/**
 * Validate session state data
 * @param data - The data to validate
 * @returns The validated and typed session state data
 * @throws ZodError if validation fails
 */
export function validateSessionState(data: unknown): SessionStateOutput {
  return sessionStateSchema.parse(data);
}

/**
 * Safely validate session state data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateSessionState(data: unknown) {
  return sessionStateSchema.safeParse(data);
}

/**
 * Check if data is valid session state data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidSessionState(data: unknown): boolean {
  return sessionStateSchema.safeParse(data).success;
}
